/* eslint-disable no-unused-vars */
import {
  all,
  put,
  call,
  delay,
  select,
  takeLatest,
  take,
  cancel,
  fork,
  actionChannel,
  flush
} from "redux-saga/effects";
import { isEmpty, get, isEqual } from "lodash";
import { addDays, format } from "date-fns";
import { getErrorScreensIcons } from "../../utils/getErrorScreenIcons/getErrorScreenIcons";
import {
  SEND_PROSPECT_TO_API,
  SEND_RO_PROSPECT_TO_API,
  sendProspectToAPISuccess,
  sendProspectToAPIFail,
  resetFormStep,
  PROSPECT_AUTO_SAVE,
  sendProspectRequest,
  sendROProspectRequest,
  SEND_PROSPECT_REQUEST,
  SEND_RO_PROSPECT_REQUEST,
  setScreeningError,
  sendRoProspectToAPISuccess,
  sendRoProspectToAPIFail,
  setLastProspectSaved,
  setLastRoProspectSaved,
  updateScreeningInfo
} from "../actions/sendProspectToAPI";
import { log } from "../../utils/loggger";
import {
  getProspect,
  getRoProspect,
  getProspectId,
  getAuthorizationHeader,
  getFinanceType,
  getIsIslamicBanking,
  getAuthToken,
  getRefreshToken
} from "../selectors/appConfig";
import { getIsEditableStatusSearchInfo } from "../selectors/searchProspect";
import { getCompletedSteps } from "../selectors/completedSteps";
import {
  getScreeningError,
  getLastProspectSaved,
  getLastRoProspectSaved,
  getIsSendingProspect
} from "../selectors/sendProspectToAPI";
import { setErrorOccurredWhilePerforming } from "../actions/searchProspect";
import { resetInputsErrors, setInputsErrors } from "../actions/serverValidation";
import { updateAccountNumbers } from "../actions/accountNumbers";
import {
  updateApplicationStatus,
  updateExistingCustomer,
  updateProspect,
  updateSubSource
} from "../actions/appConfig";
import { cloneDeep } from "../../utils/cloneDeep";
import { populateAccountCurrencies } from "../../utils/populateAccountCurrencies";
import { prospect, salesAssitRO } from "../../api/apiClient";
import {
  APP_STOP_SCREEN_RESULT,
  screeningStatus,
  screeningStatusDefault,
  CONTINUE,
  AUTO,
  VIEW_IDS,
  STEP_STATUS,
  AUTO_SAVE_INTERVAL,
  applicationdedupe,
  SCREENING_FAIL_REASONS,
  SUBMIT,
  NEXT,
  AUTOSAVE_DISABLE_ROUTES,
  sales,
  DECLINED,
  SUB_SOURCE,
  DATE_FORMAT
} from "../../constants";
import { FieldsValidationError, ErrorOccurredWhilePerforming } from "../../api/serverErrors";
import { getDefaultErrorText } from "../../utils/helper";

// Helper function to process completed steps
function processCompletedSteps(completedSteps, step) {
  const newCompletedSteps = step
    ? completedSteps.map(completedStep => {
        if (completedStep.flowId === step.flowId && completedStep.step === step.activeStep) {
          return { ...completedStep, status: STEP_STATUS.COMPLETED };
        }
        return completedStep;
      })
    : completedSteps;

  return newCompletedSteps.filter(
    (step, index, array) =>
      array.findIndex(t => t.flowId === step.flowId && t.step === step.step) === index
  );
}

export function* watchRequest() {
  const chan = yield actionChannel(SEND_PROSPECT_REQUEST);
  while (true) {
    const actions = yield flush(chan);
    if (actions.length) {
      const action = actions.find(act => act.payload.saveType === CONTINUE) || actions[0];
      yield call(sendProspectToAPI, action);
    }
    yield delay(1000);
  }
}

export function* watchRORequest() {
  const chan = yield actionChannel(SEND_RO_PROSPECT_REQUEST);
  while (true) {
    const actions = yield flush(chan);
    if (actions.length) {
      const action = actions.find(act => act.payload.saveType === CONTINUE) || actions[0];
      yield call(sendRoProspectToAPI, action);
    }
    yield delay(1000);
  }
}

export function* setScreeningResults({ preScreening }) {
  const currScreeningType = preScreening.screeningResults.find(screeningResult =>
    SCREENING_FAIL_REASONS.includes(screeningResult.screeningReason)
  );

  const screenError = screeningStatus.find(
    ({ screeningType }) => screeningType === (currScreeningType || {}).screeningType
  );
  const financeType = yield select(getFinanceType);
  if (screenError) {
    if (screenError.screeningType === screeningStatus[1].screeningType) {
      let buttons = applicationdedupe.find(
        ({ screeningNotes }) => screeningNotes === currScreeningType.screeningNotes
      );
      if (buttons !== undefined) {
        setScreeningError({
          ...screenError,
          screeningReason: get(currScreeningType, "screeningReason", ""),
          ...buttons
        });
      }
    }
    // const financeType = yield select(getFinanceType);
    const isIslamicBanking = yield select(getIsIslamicBanking);
    const { screeningType } = screenError;

    yield put(
      setScreeningError({
        ...screenError,
        screeningReason: get(currScreeningType, "screeningReason", ""),
        text: currScreeningType.reasonNotes,
        icon: getErrorScreensIcons(financeType, isIslamicBanking, screeningType)
      })
    );
  } else {
    yield put(
      setScreeningError({
        error: screeningStatusDefault.error,
        text: getDefaultErrorText(financeType),
        icon: screeningStatusDefault.icon
      })
    );
  }
}

export function* sendProspectToAPISaga({ payload: { saveType, actionType, step } }) {
  try {
    yield put(resetInputsErrors());
    yield put(resetFormStep(true));

    const prospect = cloneDeep(yield select(getProspect));
    yield put(sendProspectRequest(prospect, saveType, actionType, step));
  } finally {
    yield put(resetFormStep(false));
  }
}

export function* sendROProspectToAPISaga({ payload: { saveType, actionType, step } }) {
  try {
    yield put(resetInputsErrors());
    yield put(resetFormStep(true));

    const roProspect = yield select(getRoProspect);
    yield put(sendROProspectRequest(roProspect, saveType, actionType, step));
  } finally {
    yield put(resetFormStep(false));
  }
}

export function* prospectAutoSave(isProspectEqual) {
  try {
    if (!isProspectEqual) {
      const newProspect = cloneDeep(yield select(getProspect));
      const screeningError = yield select(getScreeningError);
      const isScreeningError = screeningError.error;
      const viewId = newProspect.applicationInfo.viewId;
      const authToken = yield select(getAuthToken);
      const refreshToken = yield select(getRefreshToken);
      const isAutoSaveEnabled =
        !isScreeningError &&
        authToken &&
        refreshToken &&
        [
          VIEW_IDS.BorrowAmount,
          VIEW_IDS.CompanyInfo,
          VIEW_IDS.StakeholdersInfo,
          VIEW_IDS.FinalQuestions,
          VIEW_IDS.UploadDocuments,
          VIEW_IDS.AecbFtsCheck,
          VIEW_IDS.PostEligibilityCompanyInfo,
          VIEW_IDS.PostEligibilityStakeholdersInfo,
          VIEW_IDS.SelectServices,
          VIEW_IDS.PostEligibilityUploadDocuments
        ].includes(viewId) &&
        !AUTOSAVE_DISABLE_ROUTES.includes(window.location.pathname);
      if (isAutoSaveEnabled) {
        yield put(sendProspectRequest(newProspect, AUTO));
      }
    }
  } catch (e) {
    log(e);
  }
}

export function* roProspectAutoSave(isRoProspectEqual) {
  try {
    if (!isRoProspectEqual) {
      const newRoProspect = cloneDeep(yield select(getRoProspect));
      const screeningError = yield select(getScreeningError);

      const isScreeningError = screeningError.error;
      const viewId = newRoProspect.applicationInfo.viewId;
      const authToken = yield select(getAuthToken);

      const isAutoSaveEnabled =
        !isScreeningError &&
        authToken &&
        [
          VIEW_IDS.BorrowAmount,
          VIEW_IDS.CompanyInfo,
          VIEW_IDS.StakeholdersInfo,
          VIEW_IDS.AecbFtsCheck,
          VIEW_IDS.PostEligibilityCompanyInfo,
          VIEW_IDS.PostEligibilityStakeholdersInfo,
          VIEW_IDS.SelectServices,
          VIEW_IDS.PostEligibilityUploadDocuments
        ].includes(viewId) &&
        !AUTOSAVE_DISABLE_ROUTES.includes(window.location.pathname);
      if (isAutoSaveEnabled) {
        yield put(sendROProspectRequest(newRoProspect, AUTO));
      }
    }
  } catch (e) {
    log(e);
  }
}

export function* sendRoProspectToAPI({ payload: { newProspect, saveType, actionType, step } }) {
  try {
    const prospectId = yield select(getProspectId);
    const headers = yield select(getAuthorizationHeader);
    if (!isEmpty(get(headers, "headers", {}))) {
      const completedSteps = yield select(getCompletedSteps);
      const nonRepetitiveCompletedSteps = processCompletedSteps(completedSteps, step);

      newProspect.applicationInfo.saveType = saveType;
      newProspect.applicationInfo.actionType = actionType;
      newProspect.freeFieldsInfo = {
        ...(newProspect.freeFieldsInfo || {}), // NOSONAR
        freeField5: JSON.stringify({ completedSteps: nonRepetitiveCompletedSteps })
      };

      const { data } = yield call(salesAssitRO.update, newProspect, headers, prospectId);

      const { preScreening } = data;

      const isScreeningError =
        preScreening && preScreening.statusOverAll === APP_STOP_SCREEN_RESULT;

      if (isScreeningError) {
        yield fork(setScreeningResults, data);
      } else if (preScreening) {
        yield put(updateProspect({ "prospect.organizationInfo.screeningInfo": preScreening }));
      }

      yield put(sendRoProspectToAPISuccess(!!isScreeningError));
      yield put(setLastRoProspectSaved(cloneDeep(newProspect)));
    }
  } catch (error) {
    if (error instanceof ErrorOccurredWhilePerforming) {
      yield put(
        setErrorOccurredWhilePerforming({
          errorCode: error.getErrorCode()
        })
      );
    } else if (error instanceof FieldsValidationError) {
      yield put(setInputsErrors(error.getInputsErrors()));
    } else {
      log(error);
    }
    yield put(sendRoProspectToAPIFail());
  }
}

export function* sendProspectToAPI({ payload: { newProspect, saveType, actionType, step } }) {
  try {
    const prospectId = yield select(getProspectId);
    const headers = yield select(getAuthorizationHeader);
    if (!isEmpty(get(headers, "headers", {}))) {
      const completedSteps = yield select(getCompletedSteps);
      const isComeFromROScreens = yield select(getIsEditableStatusSearchInfo);
      const nonRepetitiveCompletedSteps = processCompletedSteps(completedSteps, step);

      newProspect.applicationInfo.saveType = saveType;
      newProspect.applicationInfo.actionType = actionType;

      // Ensure accountCurrencies is populated before sending to API for risk score calculation
      if (newProspect.accountInfo && Array.isArray(newProspect.accountInfo)) {
        newProspect.accountInfo = populateAccountCurrencies(newProspect.accountInfo);
      }

      newProspect.freeFieldsInfo = {
        ...(newProspect.freeFieldsInfo || {}),
        freeField5: JSON.stringify({ completedSteps: nonRepetitiveCompletedSteps })
      };
      if (actionType === SUBMIT) {
        newProspect.freeFieldsInfo.freeField6 = JSON.stringify({ applicationSubmited: true });
      }
      if (actionType === sales) {
        newProspect.applicationInfo.subSource = SUB_SOURCE.SalesAssist;
        newProspect.applicationInfo.salesAssist = true;
      }
      if (newProspect.signatoryInfo[0] && newProspect.signatoryInfo[0].signatoryBankDetails) {
        let array = [];
        newProspect.signatoryInfo[0].signatoryBankDetails.forEach(sig => {
          if (!isEmpty(sig.iban) && !isEmpty(sig.bankName)) {
            const toDate = isEmpty(sig.toDate)
              ? format(addDays(new Date(), -1), DATE_FORMAT)
              : sig.toDate;
            const fromDate = isEmpty(sig.fromDate)
              ? format(addDays(new Date(), -179), DATE_FORMAT)
              : sig.fromDate;
            const new_objSig = { ...sig, fromDate, toDate };
            array.push(new_objSig);
          }
        });
        newProspect.signatoryInfo[0].signatoryBankDetails = array;
      }

      //-- Commenting below since RO is now not allowed to
      //proceed without entering Banking Details in AECB page after HD3481694

      // if (
      //   isComeFromROScreens &&
      //   newProspect.signatoryInfo[0] &&
      //   newProspect.signatoryInfo[0].signatoryBankDetails
      // ) {
      //   let array = [];
      //   newProspect.signatoryInfo[0].signatoryBankDetails.forEach(sig => {
      //     if (isEmpty(sig.iban) || isEmpty(sig.bankName)) {
      //       array.push({ bankName: "", iban: "", fromDate: "", toDate: "" });
      //     } else {
      //       array.push(sig);
      //     }
      //   });
      //   newProspect.signatoryInfo[0].signatoryBankDetails = array;
      // }

      const { signatoryBankDetails } = newProspect.signatoryInfo[0]
        ? newProspect.signatoryInfo[0]
        : {};
      if (signatoryBankDetails && signatoryBankDetails.some(sig => !isEmpty(sig.iban))) {
        newProspect.signatoryInfo.forEach(sig => {
          if (JSON.stringify(sig.signatoryBankDetails) !== JSON.stringify(signatoryBankDetails)) {
            sig.signatoryBankDetails = [...signatoryBankDetails];
          }
        });
      }

      const { data } = yield call(prospect.update, prospectId, newProspect, headers);

      if (data.accountInfo && Array.isArray(data.accountInfo)) {
        yield put(updateAccountNumbers(data.accountInfo));
        data.accountInfo.forEach((_, index) => {
          // Ensure accountCurrencies is populated for risk score API
          const accountCurrencies =
            data.accountInfo[index].accountCurrencies &&
            data.accountInfo[index].accountCurrencies.length > 0
              ? data.accountInfo[index].accountCurrencies
              : ["AED"];

          newProspect.accountInfo[index] = {
            ...newProspect.accountInfo[index],
            accountNo: data.accountInfo[index].accountNo,
            accountCurrencies: accountCurrencies
          };
        });
      }
      if (data.status || data.eligibleAmount) {
        yield put(
          updateApplicationStatus({ status: data.status, eligibleAmount: data.eligibleAmount })
        );
      }
      if (data.status) {
        yield put(updateProspect({ "prospect.applicationInfo.ipaStatus": data.status }));
      }

      const { preScreening, existingCustomer, validRoCode } = data;
      const isCompanyInfo = newProspect.applicationInfo.viewId === VIEW_IDS.CompanyInfo;
      // && newProspect.applicationInfo.saveType === NEXT;
      const isSignatoryInfo = newProspect.applicationInfo.viewId === VIEW_IDS.StakeholdersInfo;
      //&&newProspect.applicationInfo.saveType === NEXT;
      const isFinalQuestionsInfoNextStep =
        newProspect.applicationInfo.viewId === VIEW_IDS.FinalQuestions;
      const isBasicInfoNextStep =
        newProspect.applicationInfo.viewId === VIEW_IDS.BasicInformation &&
        newProspect.applicationInfo.saveType === NEXT;
      if (isBasicInfoNextStep) {
        yield put(updateExistingCustomer(existingCustomer));
        if (validRoCode === "Y") {
          yield put(updateSubSource(SUB_SOURCE.RMApply));
        }
      }
      const isScreeningError =
        preScreening && preScreening.statusOverAll === APP_STOP_SCREEN_RESULT;

      if (!isComeFromROScreens) {
        if (isScreeningError) {
          yield fork(setScreeningResults, data);
          if (isFinalQuestionsInfoNextStep && data.status === DECLINED) {
            yield put(updateProspect({ "prospect.organizationInfo.screeningInfo": preScreening }));
          }
        } else if (preScreening) {
          yield put(updateProspect({ "prospect.organizationInfo.screeningInfo": preScreening }));
        }
        if (isBasicInfoNextStep) {
          yield put(
            sendProspectToAPISuccess({
              isScreeningError: !!isScreeningError,
              existingCustomer,
              validRoCode
            })
          );
        } else {
          yield put(sendProspectToAPISuccess(!!isScreeningError));
        }
        //yield put(sendProspectToAPISuccess(!!isScreeningError));
      } else {
        yield put(sendProspectToAPISuccess(false));
        if (isCompanyInfo || isSignatoryInfo || isFinalQuestionsInfoNextStep) {
          yield put(updateScreeningInfo(data.preScreening.screeningResults));
        }
        if (preScreening) {
          yield put(updateProspect({ "prospect.organizationInfo.screeningInfo": preScreening }));
        }
      }
      yield put(setLastProspectSaved(newProspect));
    }
  } catch (error) {
    if (error instanceof ErrorOccurredWhilePerforming) {
      yield put(
        setErrorOccurredWhilePerforming({
          errorCode: error.getErrorCode()
        })
      );
    } else if (error instanceof FieldsValidationError) {
      yield put(setInputsErrors(error.getInputsErrors()));
    } else {
      log(error);
    }
    yield put(sendProspectToAPIFail());
    return error;
  }
}

export function* prospectAutoSaveFlowSaga() {
  while (true) {
    yield delay(AUTO_SAVE_INTERVAL);
    const isComeFromROScreens = yield select(getIsEditableStatusSearchInfo);
    const lastProspect = cloneDeep(yield select(getLastProspectSaved));
    const lastRoProspect = cloneDeep(yield select(getLastRoProspectSaved));
    const isSendingProspect = yield select(getIsSendingProspect);
    if (!isSendingProspect) {
      const newProspect = cloneDeep(yield select(getProspect));
      const newRoProspect = cloneDeep(yield select(getRoProspect));
      if (newProspect.freeFieldsInfo && newProspect.freeFieldsInfo.freeField5)
        newProspect.freeFieldsInfo.freeField5 = null;
      if (lastProspect.freeFieldsInfo && lastProspect.freeFieldsInfo.freeField5)
        lastProspect.freeFieldsInfo.freeField5 = null;
      if (newProspect.applicationInfo) newProspect.applicationInfo.actionType = "";
      if (lastProspect.applicationInfo) lastProspect.applicationInfo.actionType = "";
      if (newProspect.applicationInfo) newProspect.applicationInfo.saveType = "";
      if (lastProspect.applicationInfo) lastProspect.applicationInfo.saveType = "";
      const isProspectEqual = isEqual(newProspect, lastProspect);

      const bgSyncAutoSave = yield fork(prospectAutoSave, isProspectEqual);
      if (!isProspectEqual) yield take(SEND_PROSPECT_REQUEST);
      yield delay(4000);

      if (isComeFromROScreens) {
        if (newRoProspect.freeFieldsInfo && newRoProspect.freeFieldsInfo.freeField5)
          newRoProspect.freeFieldsInfo.freeField5 = null;
        if (lastRoProspect.freeFieldsInfo && lastRoProspect.freeFieldsInfo.freeField5)
          lastRoProspect.freeFieldsInfo.freeField5 = null;
        if (newRoProspect.applicationInfo) newRoProspect.applicationInfo.actionType = "";
        if (lastRoProspect.applicationInfo) lastRoProspect.applicationInfo.actionType = "";
        if (newRoProspect.applicationInfo) newRoProspect.applicationInfo.saveType = "";
        if (lastRoProspect.applicationInfo) lastRoProspect.applicationInfo.saveType = "";
        const isRoProspectEqual = isEqual(newRoProspect, lastRoProspect);

        const bgSyncROAutoSave = yield fork(roProspectAutoSave, isRoProspectEqual);
        if (!isRoProspectEqual) yield take(SEND_RO_PROSPECT_REQUEST);
        yield cancel([bgSyncAutoSave, bgSyncROAutoSave]);
      } else {
        yield cancel([bgSyncAutoSave]);
      }
    }
  }
}

export default function* sendProspectToAPISagas() {
  yield all([
    takeLatest(SEND_PROSPECT_TO_API, sendProspectToAPISaga),
    takeLatest(SEND_RO_PROSPECT_TO_API, sendROProspectToAPISaga),
    takeLatest(PROSPECT_AUTO_SAVE, prospectAutoSaveFlowSaga),
    fork(watchRequest),
    fork(watchRORequest)
  ]);
}
